import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import {
  FaCheckCircle, FaAward, FaUsers, FaTools, FaPhone, FaClock,
  FaShieldAlt, FaTrophy, FaHandshake, FaLeaf, FaMapMarkerAlt,
  FaStar, FaQuoteLeft, FaWhatsapp, FaHardHat, FaTruck, FaCubes,
  FaRupeeSign, FaThumbsUp, FaBolt, FaRecycle
} from 'react-icons/fa';
import SEO from '../components/SEO';
import { seoContent } from '../config/seoContent';
import { servicesData } from '../data/servicesData';
import { testimonialsData } from '../data/testimonialsData';
import './Home.css';

const vehicles = [
  '/images/home/banner/excavators_vehical_coimbatore.png',
  '/images/home/banner/jcb_coimbatore.png',
  '/images/home/banner/track_coimbatore.webp'
];

const Home = () => {
  const [activeVehicle, setActiveVehicle] = useState(0);
  const [dropping, setDropping] = useState(true);
  const [showSmoke, setShowSmoke] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  useEffect(() => {
    setDropping(true);
    setShowSmoke(false);
    const smokeTimer = setTimeout(() => setShowSmoke(true), 600);
    const switchTimer = setTimeout(() => {
      setShowSmoke(false);
      setDropping(false);
      setTimeout(() => {
        setActiveVehicle(prev => (prev + 1) % vehicles.length);
        setDropping(true);
      }, 500);
    }, 5000);
    return () => { clearTimeout(smokeTimer); clearTimeout(switchTimer); };
  }, [activeVehicle]);

  const stats = [
    { icon: <FaCheckCircle />, number: '500+', label: 'Projects Completed' },
    { icon: <FaUsers />, number: '300+', label: 'Happy Clients' },
    { icon: <FaAward />, number: '15+', label: 'Years Experience' },
    { icon: <FaTools />, number: '50+', label: 'Expert Team' }
  ];

  const features = [
    { icon: <FaShieldAlt />, title: 'Safety First', desc: 'Complete safety compliance in all operations with certified protocols' },
    { icon: <FaTrophy />, title: 'Quality Assured', desc: 'Premium materials and expert workmanship on every project' },
    { icon: <FaClock />, title: 'On-Time Delivery', desc: 'Projects and material deliveries completed within deadline' },
    { icon: <FaHandshake />, title: 'Trusted Partner', desc: '15+ years of reliable service across Coimbatore' },
    { icon: <FaLeaf />, title: 'Eco-Friendly', desc: 'Sustainable practices and environment-conscious operations' },
    { icon: <FaPhone />, title: '24/7 Support', desc: 'Always available for your queries and emergencies' }
  ];

  const highlights = [
    { icon: <FaHardHat />, title: 'Civil Services', desc: 'Demolition & Earthwork', color: '#2E7D32' },
    { icon: <FaTruck />, title: 'Equipment Rental', desc: 'JCB, Excavator & More', color: '#1976D2' },
    { icon: <FaCubes />, title: 'Building Materials', desc: 'M Sand, P Sand & More', color: '#FBC02D' },
  ];

  return (
    <>
      <SEO
        title={seoContent.home.title}
        description={seoContent.home.description}
        keywords={seoContent.home.keywords}
      />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-split">
            <div className="hero-content" data-aos="fade-right">
              <div className="hero-badge">Coimbatore's #1 Civil Supplier</div>
              <h1>SRI KRISH CIVIL SUPPLIER</h1>
              <p className="hero-subtitle">Your Trusted Partner in Civil Construction</p>
              <p className="hero-description">
                Leading provider of building demolition, earthwork services, equipment rentals, and premium building materials in Coimbatore. Serving 300+ happy clients with 15+ years of expertise.
              </p>
              <div className="hero-highlights">
                {highlights.map((h, i) => (
                  <div key={i} className="hero-highlight-item">
                    <span style={{ color: h.color }}>{h.icon}</span>
                    <div>
                      <strong>{h.title}</strong>
                      <small>{h.desc}</small>
                    </div>
                  </div>
                ))}
              </div>

            </div>
            <div className="hero-animation">
              <div className={`vehicle-wrapper ${dropping ? 'drop-in' : 'drop-out'}`}>
                <img src={vehicles[activeVehicle]} alt="Civil construction vehicle Coimbatore" className="vehicle-img" />
                {showSmoke && (
                  <div className="smoke-container">
                    {[...Array(16)].map((_, i) => <span key={i} className={`smoke s${i + 1}`}></span>)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="stat-icon">{stat.icon}</div>
                <h3>{stat.number}</h3>
                <p>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <h2>Our Civil Services</h2>
            <p>Professional building demolition, earthwork, and site preparation services in Coimbatore</p>
          </div>
          <div className="home-services-list">
            {servicesData.filter(s => s.category === 'services').map((service, index) => (
              <div key={service.id} className="service-row-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="service-row-image"><img src={service.image} alt={service.title} /></div>
                <div className="service-row-content">
                  <h2>{service.title}</h2>
                  <p>{service.shortDesc}</p>
                </div>
                <div className="service-row-action">
                  <Link to={`/services/${service.id}`} className="btn btn-primary">Learn More</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rentals Section */}
      <section className="services-section rentals-section">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <h2>Equipment for Rent in Coimbatore</h2>
            <p>Hire JCB, Excavator, Tipper, Bobcat, Road Roller and more — hourly, daily & weekly rates</p>
          </div>
          <div className="home-services-list">
            {servicesData.filter(s => s.category === 'rentals').map((service, index) => (
              <div key={service.id} className="service-row-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="service-row-image"><img src={service.image} alt={service.title} /></div>
                <div className="service-row-content">
                  <h2>{service.title}</h2>
                  <p>{service.shortDesc}</p>
                </div>
                <div className="service-row-action">
                  <Link to={`/services/${service.id}`} className="btn btn-primary">Learn More</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Building Materials Section */}
      <section className="services-section materials-section">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <h2>Building Materials Supplier in Coimbatore</h2>
            <p>M Sand, P Sand, Wet Mix Macadam, Coarse Aggregates, Red Soil & Gravel — bulk supply & delivery</p>
          </div>
          <div className="home-services-list">
            {servicesData.filter(s => s.category === 'materials').map((service, index) => (
              <div key={service.id} className="service-row-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="service-row-image"><img src={service.image} alt={service.title} /></div>
                <div className="service-row-content">
                  <h2>{service.title}</h2>
                  <p>{service.shortDesc}</p>
                </div>
                <div className="service-row-action">
                  <Link to={`/services/${service.id}`} className="btn btn-primary">Learn More</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-choose-section">
        <div className="container">
          <div className="why-choose-content">
            <div className="why-choose-text" data-aos="fade-right">
              <h2>Why Choose SRI KRISH CIVIL SUPPLIER?</h2>
              <p style={{color:'var(--text-light)', marginBottom:'20px', lineHeight:'1.8'}}>
                With over 15 years of experience in civil construction across Coimbatore, we have built a reputation for quality, reliability, and professionalism. From small residential projects to large commercial developments, we deliver excellence every time.
              </p>
              <ul className="features-list">
                <li><FaCheckCircle /> 15+ years of industry experience in Coimbatore</li>
                <li><FaCheckCircle /> Licensed, insured & safety-certified operations</li>
                <li><FaCheckCircle /> Modern equipment fleet — JCB, Excavator, Bobcat & more</li>
                <li><FaCheckCircle /> Expert and professional team of 50+ members</li>
                <li><FaCheckCircle /> IS-standard quality building materials</li>
                <li><FaCheckCircle /> Competitive pricing with no hidden charges</li>
                <li><FaCheckCircle /> Timely project completion — guaranteed</li>
                <li><FaCheckCircle /> 24/7 customer support & emergency response</li>
                <li><FaCheckCircle /> Eco-friendly and sustainable practices</li>
                <li><FaCheckCircle /> Free site inspection and detailed quotation</li>
              </ul>
              <div style={{display:'flex', gap:'15px', flexWrap:'wrap', marginTop:'25px'}}>
                <Link to="/about" className="btn btn-primary">About Us</Link>
                <Link to="/contact" className="btn btn-secondary">Get Free Quote</Link>
              </div>
            </div>
            <div className="why-choose-image" data-aos="fade-left">
              <img src="/images/home/why_choose_sri_krish_civil_supplier.webp" alt="Why Choose SRI KRISH CIVIL SUPPLIER Coimbatore" />
              <div className="why-choose-badge">
                <FaThumbsUp />
                <span>300+ Happy Clients</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Banner */}
      <section className="value-props-section">
        <div className="container">
          <div className="value-props-grid">
            <div className="value-prop" data-aos="fade-up">
              <FaRupeeSign />
              <h3>Best Rates</h3>
              <p>Competitive pricing on all services and materials with no hidden charges</p>
            </div>
            <div className="value-prop" data-aos="fade-up" data-aos-delay="100">
              <FaBolt />
              <h3>Fast Delivery</h3>
              <p>Quick mobilization and on-time material delivery across Coimbatore</p>
            </div>
            <div className="value-prop" data-aos="fade-up" data-aos-delay="200">
              <FaRecycle />
              <h3>Eco-Friendly</h3>
              <p>Sustainable construction practices and responsible waste management</p>
            </div>
            <div className="value-prop" data-aos="fade-up" data-aos-delay="300">
              <FaShieldAlt />
              <h3>100% Safe</h3>
              <p>Certified safety protocols protecting your site, team, and surroundings</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <h2>What Our Clients Say</h2>
            <p>Trusted by 300+ satisfied customers across Coimbatore</p>
          </div>
          <div className="testimonials-grid">
            {testimonialsData.slice(0, 3).map((testimonial, index) => (
              <div key={testimonial.id} className="testimonial-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <FaQuoteLeft className="quote-icon" />
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} style={{ color: '#FBC02D', marginRight: '3px' }} />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar-small">{testimonial.name.charAt(0)}</div>
                  <div>
                    <h4>{testimonial.name}</h4>
                    <p>{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center" style={{marginTop: '40px'}}>
            <Link to="/testimonials" className="btn btn-secondary">View All Testimonials</Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <h2>Why We Stand Out in Coimbatore</h2>
            <p>Our commitment to excellence sets us apart in every project we undertake</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card" data-aos="zoom-in" data-aos-delay={index * 100}>
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <h2>Our Work Process</h2>
            <p>Simple, transparent, and efficient — from first call to project completion</p>
          </div>
          <div className="process-timeline">
            {[
              { n: 1, title: 'Contact Us', desc: 'Call, WhatsApp, or fill our contact form — we respond within 2 hours', dir: 'fade-right' },
              { n: 2, title: 'Free Site Inspection', desc: 'Our expert visits your site for a thorough assessment at no cost', dir: 'fade-left' },
              { n: 3, title: 'Detailed Quote', desc: 'Transparent quotation with itemized pricing — no hidden charges', dir: 'fade-right' },
              { n: 4, title: 'Project Execution', desc: 'Professional execution with daily progress updates and quality checks', dir: 'fade-left' },
              { n: 5, title: 'Handover & Support', desc: 'Timely delivery, site cleanup, and post-service support included', dir: 'fade-right' },
            ].map(step => (
              <div key={step.n} className="process-step" data-aos={step.dir}>
                <div className="step-number">{step.n}</div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="service-areas-section">
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <h2>Areas We Serve in Coimbatore</h2>
            <p>Providing quality civil services and material delivery across all Coimbatore zones</p>
          </div>
          <div className="areas-content" data-aos="zoom-in">
            <div className="areas-grid">
              {[
                'Coimbatore City', 'RS Puram', 'Gandhipuram', 'Saravanampatti',
                'Peelamedu', 'Singanallur', 'Kuniyamuthur', 'Vadavalli',
                'Ganapathy', 'Podanur', 'Sulur', 'Thudiyalur',
                'Kovaipudur', 'Ondipudur', 'Kalapatti', 'All Coimbatore Areas'
              ].map((area, i) => (
                <div key={i} className="area-item"><FaMapMarkerAlt /> {area}</div>
              ))}
            </div>
            <p className="areas-note">Serving all areas in and around Coimbatore with prompt and reliable service. Call us for availability in your area.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content" data-aos="zoom-in">
            <h2>Ready to Start Your Project in Coimbatore?</h2>
            <p>Get a free site inspection and detailed quote today. Our team responds within 2 hours.</p>
            <div className="cta-contact-info">
              <span><FaPhone /> 9894065756</span>
              <span><FaWhatsapp /> WhatsApp Available</span>
              <span><FaClock /> Mon–Sat: 8AM–6PM</span>
            </div>
            <div className="cta-buttons">
              <a href="tel:9894065756" className="btn btn-primary"><FaPhone /> Call Now</a>
              <a href="https://wa.me/919894065756" className="btn btn-whatsapp" target="_blank" rel="noopener noreferrer"><FaWhatsapp /> WhatsApp</a>
              <Link to="/contact" className="btn btn-secondary">Get Free Quote</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
